import type { Paragraph, Run, Spread, Story } from '@proofroom/schema';

/** Key-sorted JSON so two run overrides built in a different order still compare equal. */
function stable(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  const keys = Object.keys(value as Record<string, unknown>).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stable((value as Record<string, unknown>)[k])}`).join(',')}}`;
}

/**
 * No overrides and an empty overrides object are the same thing. A room round trip emits `{}` where
 * the draft had none, and comparing those as different reprinted an untouched story from the engine.
 */
function overrideKey(overrides: Run['overrides']): string {
  return !overrides || Object.keys(overrides).length === 0 ? '{}' : stable(overrides);
}

function runsDiffer(a: Run[], b: Run[]): boolean {
  if (a.length !== b.length) return true;
  return a.some((r, i) => {
    const s = b[i];
    return r.start !== s.start || r.end !== s.end || r.charStyleId !== s.charStyleId || overrideKey(r.overrides) !== overrideKey(s.overrides);
  });
}

/**
 * Paragraph `overrides` are deliberately not compared. Both materialisers restore them from the
 * draft by paragraph id rather than carrying them through the room, since the editor cannot author
 * paragraph formatting, so they can only ever match; a paragraph the editor really did add shows up
 * in the count instead. The viewer's own storiesDiffer leaves them out for the same reason.
 */
function paragraphsDiffer(a: Paragraph[], b: Paragraph[]): boolean {
  if (a.length !== b.length) return true;
  return a.some((p, i) => {
    const q = b[i];
    return p.id !== q.id || p.styleId !== q.styleId || p.text !== q.text || runsDiffer(p.runs, q.runs);
  });
}

/**
 * The current stories that no longer match the draft, text or formatting. Only these need the
 * engine: an untouched story prints InDesign's own lines, which are already in the snapshot.
 */
export function differingStories(draft: Story[], current: Story[]): Story[] {
  const byId = new Map(draft.map((s) => [s.id, s]));
  return current.filter((s) => {
    const base = byId.get(s.id);
    return !base || paragraphsDiffer(base.paragraphs, s.paragraphs);
  });
}

/** Sheet size in points: a spread prints as one sheet, so a two page spread is a double width sheet. */
export function sheetSize(spread: Spread): { w: number; h: number } {
  return { w: spread.bounds.w, h: spread.bounds.h };
}

/** Spread indexes named by `?spreads=`: `all`, a single index, or nothing at all. */
export function spreadSelection(param: string | null, count: number): number[] {
  if (param === 'all') return Array.from({ length: count }, (_, i) => i);
  if (!param || !/^\d+$/.test(param)) return [];
  const n = Number(param);
  return n < count ? [n] : [];
}
